import { ColumnsType } from "antd/es/table";
import { T_Populate } from "types/_utils.types";
import { FileSchemaDto } from "dtos/file.dto";
import { LedgerSchemaDto } from "dtos/ledger.dto";
import { LoanSchemaDto } from "dtos/loan.dto";
import { formatMoney } from "utils/functions";
import { T_FormValues } from "./CalculatorExecute";
import { NegotiationDto } from "dtos/communication.dto";

export const detailedLedgerColumns = (
  loans: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[] | undefined,
  cycle: "weekly" | "monthly",
): ColumnsType<LedgerSchemaDto> => {
  return [
    {
      key: "week",
      dataIndex: "week",
      title: cycle === "monthly" ? "Month" : "Week",
      width: 80,
      render: (val) => (val === 0 ? "Initial" : val),
    },
    {
      key: "payment",
      dataIndex: "amount",
      title: "Payment",
      align: "right",
      render: (val) => (typeof val === "number" ? formatMoney(val) : "-"),
    },
    {
      key: "program_fee",
      dataIndex: "program_fee",
      title: "Program Fee",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.program_fee === "number" ? formatMoney(record?.distribution?.program_fee) : "-",
    },
    {
      key: "service_fee",
      dataIndex: "service_fee",
      title: "Service Fee",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.service_fee === "number" ? formatMoney(record?.distribution?.service_fee) : "-",
    },
    {
      key: "legal_fee",
      dataIndex: "legal_fee",
      title: "Legal Fee",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.legal_fee === "number" ? formatMoney(record?.distribution?.legal_fee ?? "") : "-",
    },
    {
      key: "creditor",
      dataIndex: "creditor",
      title: "Creditor Total",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.creditor_total === "number"
          ? formatMoney(record?.distribution?.creditor_total ?? "")
          : "-",
    },
    {
      key: "creditors",
      dataIndex: "creditor",
      title: "Creditors",
      align: "center",
      children: loans?.map((el) => ({
        title: el?.creditor_name,
        key: el?._id,
        align: "right",
        render: (val, record) => {
          const thisCreditor = record?.creditors?.find((creditor) => creditor?.creditor_id === el?._id);
          return <>{thisCreditor ? formatMoney(thisCreditor?.amount) : "-"}</>;
        },
        // typeof record?.creditors?.?.creditor_total === "number"
        //   ? formatMoney(record?.distribution?.creditor_total)
        //   : "-",
      })),
    },
  ];
};

export const detailedLedgerColumnsForPreview = (
  loans: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[] | undefined,
): ColumnsType<LedgerSchemaDto> => {
  return [
    {
      key: "week",
      dataIndex: "week",
      title: "Week",
      width: 80,
      render: (val) => (val === 0 ? "Initial" : val),
    },
    {
      key: "payment",
      dataIndex: "amount",
      title: "Payment",
      align: "right",
      render: (val) => (typeof val === "number" ? formatMoney(val) : "-"),
    },
    {
      key: "intendened_payment",
      dataIndex: "",
      title: "Intended Payment",
      align: "right",
      render: (val, record) => {
        //this will be summation of program_fee, service_fee, legal_fee
        const intended_payment =
          (typeof record?.distribution?.program_fee === "number" ? record?.distribution?.program_fee : 0) +
          (typeof record?.distribution?.service_fee === "number" ? record?.distribution?.service_fee : 0) +
          (typeof record?.distribution?.legal_fee === "number" ? record?.distribution?.legal_fee : 0);
        return typeof intended_payment === "number" ? formatMoney(intended_payment) : "-";
      },
    },
    {
      key: "program_fee",
      dataIndex: "program_fee",
      title: "Program Fee",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.program_fee === "number" ? formatMoney(record?.distribution?.program_fee) : "-",
    },
    {
      key: "service_fee",
      dataIndex: "service_fee",
      title: "Service Fee",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.service_fee === "number" ? formatMoney(record?.distribution?.service_fee) : "-",
    },
    {
      key: "legal_fee",
      dataIndex: "legal_fee",
      title: "Legal Fee",
      align: "right",
      render: (val, record) =>
        typeof record?.distribution?.legal_fee === "number" ? formatMoney(record?.distribution?.legal_fee ?? "") : "-",
    },
    {
      key: "creditor",
      dataIndex: "creditor",
      title: "Creditor Total",
      align: "right",
      render: (val, record) => {
        const total = (record?.creditors || []).reduce(
          (sum: number, c: { amount: number | null }) => sum + (c.amount ?? 0),
          0,
        );

        return total ? formatMoney(total) : "-";
      },
    },
    {
      key: "creditors",
      dataIndex: "creditor",
      title: "Creditors",
      align: "center",
      children: loans?.map((el) => ({
        title: el?.creditor_name,
        key: el?._id,
        align: "right",
        render: (val, record) => {
          const thisCreditor = record?.creditors?.find(
            (creditor: { creditor_id: string }) => creditor?.creditor_id === el?._id,
          );
          return <>{thisCreditor && thisCreditor.amount ? formatMoney(thisCreditor?.amount) : "-"}</>;
        },
      })),
    },
  ];
};

export const negotiationInitialValue: NegotiationDto = {
  context: {
    objective: "delay",
    type: "negotiation",
    history: {
      round_number: 1,
      creditor_last_tone: null,
      creditor_last_offer: null,
      debtor_floor: 750,
      debtor_ceiling: 14560,
    },
    negotiate_as: "debtor",
    paid_to: "creditor",
    stage: "initial",
    rationale: [
      "Immediate funds available for payment",
      "Avoids additional charge-off costs for creditor",
      "Temporary cashflow issues due to seasonal sales dip",
    ],
    priority: "high",
    llm_input: "openai",
    hard_deadlines: [{ label: "charge_off_date", date: "2025-09-15" }],
    hardship_flags: ["seasonality", "temporary_cash_crunch", "debt_overburden"],
    notes: "Initial outreach for settlement.",
    engage_legal_team: false,
  },
  config: {
    tonality: {
      empathy: "low",
      rigidity: "high",
      system_agression: 5,
      formality: "business_formal",
      urgency_level: "medium",
      persuasion_mode: "Emotional (defensive), low logic engagement",
      emotion_focus: ["resentiment", "frustation", "irony"],
      sentence_style: " Indirect, qualified statements (“I thought this was already clear…”)",
      politeness: "superficially high (formal politeness masking frustration)",
    },
    locale: "en-us",
    currency: "USD",
    message_generation: {
      style_guide: {
        voice: "professional_calming",
        constraints: { max_paragraphs: 5, max_words_per_email: 350 },
        must_include: [
          "reference_balance_and_context",
          "specific_offer_and_rationale",
          "call_to_action_with_deadline",
          "compliance_footer",
        ],
        avoid: ["legal_threats", "personal_attacks", "overpromising"],
      },
      data_points_to_surface: [
        "immediate_lump_sum_available",
        "cost_of_delay_for_creditor",
        "operational_constraints",
        "creditharm_mitigation",
      ],
    },
    constraints_and_guardrails: {
      compliance: {
        no_admissions_of_liability: true,
        no_unauthorized_legal_advice: true,
        respect_opt_out: true,
        include_disclaimers: true,
      },
      sensitivity: {
        avoid_threatening_language: true,
        avoid_personal_data_requests: true,
      },
      redlines_and_stoppers: {
        absolute_max_cash_offer_percent: 0.48,
        no_confession_of_judgment: true,
        no_additional_collateral: true,
        no_personal_guarantee_additions: true,
      },
    },
    strategy_config: {
      anchor_strategy: {
        initial_anchor_percent_of_balance: undefined,
        justification_bullets: [
          "seasonal cashflow volatility",
          "immediate funds availability",
          "avoids charge-off costs",
        ],
      },
      restructuring_terms: {
        max_term_weeks: undefined,
        apr_cap: undefined,
        balloon_allowed: undefined,
        late_fee_waiver_expected: undefined,
        min_down_payment_percent: undefined,
        grace_period_days: 1,
        interest_waiver_percent_target: undefined,
      },
      delay_path: {
        eligible: true,
        criteria: {
          min_dscr: 1.05,
          recent_delinquency_days_max: 60,
        },
        options: [
          { type: "short_extension", days: 30 },
          { type: "forbearance", days: 60 },
        ],
      },
      settlement_band: {
        floor_percent: undefined,
        target_percent: undefined,
        ceiling_percent: undefined,
        initial_payment: undefined,
        weekly_required: false,
      },
    },
  },
};
